import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Table, Input, Space, Tag, message } from "antd";
import TagModal from "../../components/Tags/TagModal";
import Layout from '../../Layout'
import { mixGetV1,mixDate, mixPutV1 } from "services/mixin";
import { Edit } from "lucide-react";
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { Search } = Input;

interface TagItem {
    id: number;
    name: string;
    code: string;
    position: number;
    color: string;
    creator: {
        id: number,
        email: string;
        fullname: string;
    };
    description: string;
    usage_count: number;
    created_at?: string | null;
}

export default function CustomerTags() {
  const { t } = useTranslation();
    // Phan quyen thao tac: chi co quyen xem thi khong hien nut sua
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canEdit = isSuperAdmin || hasPermission("sale_tags.edit");
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<"add" | "edit">("add");
    const [editingItem, setEditingItem] = useState<TagItem | null>(null);

    const [data, setData] = useState<TagItem[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sortParam, setSortParam] = useState<string>("position,desc");

    const openEdit = (record: TagItem) => {
        setMode("edit");
        setEditingItem(record);
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {

        const param = {...values}

        if (mode === "edit" && editingItem) {
            const res = await mixPutV1(`/api/v1/be/customer-tag/${editingItem.id}`, param)

            if (res.status) {
                message.success(t('customer_tags.edit_success'));
                getList()
            } else {
                message.error(t('customer_tags.edit_failed'));
            }
        }

        setModalOpen(false);
    };

    const columns: SettingColumn<TagItem>[] = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            sorter: true,
        },
        {
            title: t('customer_tags.col_tag_name'),
            key: "name",
            sorter: true,
            render: (_, record) => (
                <Tag color={record.color}>{record.name}</Tag>
            ),
        },
        {
            title: 'Mã thẻ',
            key: "code",
            sorter: true,
            render: (_, record) => (
                <Tag color={record.color}>{record.code}</Tag>
            ),
        },
        {
            title: 'Vị trí',
            dataIndex: "position",
            key: "position",
            sorter: true,
        },
        {
            title: t('customer_tags.col_created_at'),
            key: "created_at",
            sorter: true,
            render: (_, record) => mixDate(record.created_at, {hour: true}),
        },
        {
            key: "creator",
            title: t('customer_tags.col_creator'),
            render: (_, record) => (record.creator?.fullname),
        },
        {
            title: t('customer_tags.col_description'),
            dataIndex: "description",
            width: 260,
            ellipsis: { showTitle: false },
            render: (text: string) => (
                <span title={text} className="block truncate max-w-[240px]">{text}</span>
            ),
        },
        {
            title: t('customer_tags.col_usage_count'),
            dataIndex: "usage_count",
            key: "usage_count",
            sorter: true,
        },
        {
            key: "actions",
            title: t('customer_tags.col_action'),
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <div className="flex items-center space-x-3 gap-2">
                        {canEdit && (
                            <button
                                onClick={() => openEdit(record)}
                                className="!text-[#3e91ab] hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 p-1.5 rounded-md transition-colors"
                                title="View Details"
                            >
                                <Edit className="h-4 w-4" />
                            </button>
                        )}

                    </div>
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "customer_tag_list",
        canEdit ? columns : columns.filter((c) => c.key !== "actions")
    );

    // ---------------  API------------------------
    const getList = async () => {
        setLoading(true);

        const param = {
            page,
            search: searchTerm,
            sort: sortParam,
        }

        const res = await mixGetV1('/api/v1/be/customer-tags', param)

        if (res.status) {
            setData(res.data.items)
            setTotal(res.data.total)
        }
        setLoading(false);
    }



    useEffect(() => {
        getList()
    },[page, searchTerm, sortParam])

    // useEffect(() => {
    //     console.log(data)
    // },[data])
    return (
        <Layout>
            <Helmet>
                <title>{t('page.customer_tags')} | Khothi</title>
            </Helmet>
            <div className="">
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg font-semibold t-text-white">
                        {t('customer_tags.title')}
                    </h2>

                    <Space>
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400" aria-hidden="true"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></svg>
                            </div>
                            <input
                                type="text"
                                onChange={(e) => {
                                    setSearchTerm(e.target.value)
                                    setPage(1)
                                }}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 t-text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#3e91ab] focus:border-[#3e91ab] sm:text-sm transition-colors h-[32px]"
                                placeholder={t('customer_tags.search_placeholder')}
                                
                            />
                        </div>

                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    loading={loading}
                    rowKey="id"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    onChange={(_pagination, _filters, sorter: any, extra: any) => {
                        if (extra?.action === 'sort') {
                            if (sorter?.columnKey && sorter?.order) {
                                const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                                setSortParam(`${sorter.columnKey},${direction}`);
                            } else {
                                setSortParam('id,desc');
                            }
                            setPage(1);
                        }
                    }}
                    pagination={{
                        current: page,
                        total: total,
                        pageSize: 10,
                        onChange: (p) => setPage(p),
                    }}
                />

                <TagModal
                    open={modalOpen}
                    mode={mode}
                    initialValues={
                        editingItem
                            ? {
                                name: editingItem.name,
                                code: editingItem.code,
                                position: editingItem.position,
                                color: editingItem.color,
                                description: editingItem.description,
                            }
                            : undefined
                    }
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                />

            </div>
        </Layout>
    );
}